<template>
  <Layout>
    <Aguarde :show="httpSend && !carregando"/>

    <div class="fisc-sn">

      <!-- ─── Cabeçalho ──────────────────────────────────────── -->
      <div class="page-intro">
        <div class="page-intro__text">
          <h2 class="page-intro__title">Apuração do Simples Nacional</h2>
          <p class="page-intro__sub">
            Divergências identificadas, impacto financeiro estimado e priorização da ação fiscal.
          </p>
        </div>
        <div class="page-intro__actions">
          <span v-if="ultimaAtualizacao && !carregando" class="badge-live">
            <span class="live-dot"/>
            Atualizado às {{ ultimaAtualizacao }}
          </span>
          <button type="button" class="btn-atualizar" :disabled="httpSend" @click="pesquisar">
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
              <path d="M14 8a6 6 0 11-1.76-4.24" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M14 2v4h-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                    stroke-linejoin="round"/>
            </svg>
            {{ httpSend ? 'Atualizando...' : 'Atualizar' }}
          </button>
        </div>
      </div>

      <!-- ─── Erro na consulta ───────────────────────────────── -->
      <div v-if="erro" class="alert-erro">
        <div class="alert-erro__icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="9"/>
            <path d="M12 8v5M12 16h.01"/>
          </svg>
        </div>
        <div class="alert-erro__body">
          <p class="alert-erro__title">Não foi possível carregar as divergências</p>
          <p class="alert-erro__desc">{{ erro }}</p>
        </div>
        <button type="button" class="alert-erro__btn" :disabled="httpSend" @click="pesquisar">Tentar novamente</button>
      </div>

      <!-- ─── Skeleton ───────────────────────────────────────── -->
      <div v-if="carregando" class="skeleton-wrap">
        <div class="sk-metrics">
          <div class="sk-metric sk-metric--wide"/>
          <div v-for="n in 4" :key="`skm-${n}`" class="sk-metric"/>
        </div>
        <div class="sk-charts">
          <div v-for="n in 4" :key="`skc-${n}`" class="sk-chart"/>
        </div>
        <div class="sk-table">
          <div v-for="n in 8" :key="`skr-${n}`" class="sk-row"/>
        </div>
      </div>

      <template v-else>

        <!-- ─── Indicadores ──────────────────────────────────── -->
        <div class="metrics-grid">

          <!-- Indicador principal: potencial de arrecadação -->
          <div class="metric-card metric-card--destaque">
            <div class="metric-destaque__top">
              <div class="metric-icon metric-icon--destaque">
                <span v-html="iconMoney()"/>
              </div>
              <span class="metric-destaque__tag">Prioridade financeira</span>
            </div>
            <div>
              <p class="metric-destaque__label">Potencial de arrecadação</p>
              <p class="metric-destaque__value">{{ formatMoeda(totalPotencial) }}</p>
              <p class="metric-destaque__desc">
                Potencial estimado de arrecadação identificado nas divergências.
              </p>
            </div>
            <div v-if="maiorPotencial" class="metric-destaque__footer">
              <span class="metric-destaque__footer-label">Maior potencial individual</span>
              <span class="metric-destaque__footer-value">
                {{ maiorPotencial.nomeExibicao }}
                <span class="metric-destaque__footer-cnpj">{{ maiorPotencial.cnpjFormatado }}</span>
                — {{ formatMoeda(maiorPotencial.potencial) }}
              </span>
            </div>
          </div>

          <div
              v-for="indicador in indicadores"
              :key="indicador.key"
              class="metric-card"
              :class="[`metric-card--${indicador.color}`, indicador.acao ? 'metric-card--clicavel' : '']"
              @click="indicador.acao && indicador.acao()"
          >
            <div class="metric-icon">
              <span v-html="indicador.icon"/>
            </div>
            <div class="metric-body">
              <p class="metric-label">{{ indicador.label }}</p>
              <p class="metric-value">{{ indicador.value }}</p>
              <p v-if="indicador.sub" class="metric-sub">{{ indicador.sub }}</p>
            </div>
          </div>
        </div>

        <!-- ─── Gráficos ─────────────────────────────────────── -->
        <div v-show="registrosFiltrados.length > 0" ref="chartsGrid" class="charts-grid">

          <div class="chart-card">
            <div class="chart-header">
              <div>
                <p class="chart-title">Maiores potenciais de arrecadação</p>
                <p class="chart-sub">Top {{ topPotencial.length }} empresas por potencial estimado</p>
              </div>
            </div>
            <div v-if="topPotencial.length > 0" ref="chartPotencial" class="chart-container chart-container--tall"/>
            <p v-else class="chart-vazio">Nenhuma empresa com potencial de arrecadação nos filtros aplicados</p>
          </div>

          <div class="chart-card">
            <div class="chart-header">
              <div>
                <p class="chart-title">Maiores diferenças identificadas</p>
                <p class="chart-sub">Top {{ topDiferenca.length }} empresas por valor de diferença</p>
              </div>
            </div>
            <div v-if="topDiferenca.length > 0" ref="chartDiferenca" class="chart-container chart-container--tall"/>
            <p v-else class="chart-vazio">Nenhuma diferença apurada nos filtros aplicados</p>
          </div>

          <div class="chart-card">
            <div class="chart-header">
              <div>
                <p class="chart-title">Concentração das divergências</p>
                <p class="chart-sub">Empresas por faixa de quantidade de divergências</p>
              </div>
            </div>
            <div v-if="totalDivergencias > 0" ref="chartFaixas" class="chart-container"/>
            <p v-else class="chart-vazio">Nenhuma divergência nos filtros aplicados</p>
          </div>

          <div class="chart-card">
            <div class="chart-header">
              <div>
                <p class="chart-title">Potencial x diferença</p>
                <p class="chart-sub">Relação entre a diferença apurada e o potencial estimado</p>
              </div>
              <div class="chart-total">
                <p class="chart-total-label">Potencial médio</p>
                <p class="chart-total-value">{{ percentualPotencialFormatado }}</p>
              </div>
            </div>
            <div v-if="pontosRelacao.length > 0" ref="chartRelacao" class="chart-container"/>
            <p v-else class="chart-vazio">Sem valores para comparar nos filtros aplicados</p>
          </div>
        </div>

        <!-- ─── Filtros ──────────────────────────────────────── -->
        <div class="filtros-card">
          <div class="filtros-header">
            <div class="header-left-group">
              <p class="chart-title">Filtros</p>
              <span v-if="filtrosAtivos > 0" class="badge-count badge-count--ativo">
                {{ filtrosAtivos }} {{ filtrosAtivos === 1 ? 'filtro ativo' : 'filtros ativos' }}
              </span>
            </div>
            <button v-if="filtrosAtivos > 0" type="button" class="btn-limpar" @click="limparFiltros">
              Limpar filtros
            </button>
          </div>

          <div class="filtros-grid">
            <div class="campo">
              <label for="f-cnpj">CNPJ / CPF</label>
              <input id="f-cnpj" v-model="filtros.cnpj" class="campo-input" type="text"
                     placeholder="Somente números" autocomplete="off">
            </div>
            <div class="campo">
              <label for="f-nome">Empresa</label>
              <input id="f-nome" v-model="filtros.nome" class="campo-input" type="text"
                     placeholder="Razão social / nome" autocomplete="off">
            </div>
            <div class="campo">
              <label for="f-atividade">Código</label>
              <input id="f-atividade" v-model="filtros.atividade" class="campo-input" type="text"
                     placeholder="Código" autocomplete="off">
            </div>
            <div class="campo">
              <label for="f-divergencias">Divergências (mínimo)</label>
              <input id="f-divergencias" v-model="filtros.minDivergencias" class="campo-input" type="number"
                     min="0" step="1" placeholder="0">
            </div>
            <div class="campo">
              <label for="f-diferenca">Diferença mínima (R$)</label>
              <input id="f-diferenca" v-model="filtros.minDiferenca" class="campo-input" type="number"
                     min="0" step="0.01" placeholder="0,00">
            </div>
            <div class="campo">
              <label for="f-potencial">Potencial mínimo (R$)</label>
              <input id="f-potencial" v-model="filtros.minPotencial" class="campo-input" type="number"
                     min="0" step="0.01" placeholder="0,00">
            </div>
            <div class="campo">
              <label for="f-cadastro">Situação cadastral</label>
              <select id="f-cadastro" v-model="filtros.cadastro" class="campo-input">
                <option value="todos">Todas as empresas</option>
                <option value="cadastradas">Somente cadastradas</option>
                <option value="nao_cadastradas">Somente não cadastradas</option>
              </select>
            </div>
          </div>
        </div>

        <!-- ─── Tabela ───────────────────────────────────────── -->
        <div ref="tabela" class="table-card">
          <div class="table-header">
            <div class="header-left-group">
              <p class="chart-title">Empresas com divergência</p>
              <span class="badge-count">
                {{ formatInteiro(registrosFiltrados.length) }}
                {{ registrosFiltrados.length === 1 ? 'registro' : 'registros' }}
              </span>
              <span v-if="registrosFiltrados.length !== registros.length" class="badge-count badge-count--ativo">
                de {{ formatInteiro(registros.length) }}
              </span>
            </div>
            <div class="header-right-group">
              <div class="busca-wrap">
                <svg class="busca-icon" width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <circle cx="7" cy="7" r="5" stroke="currentColor" stroke-width="1.4"/>
                  <path d="M10.8 10.8L14 14" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
                </svg>
                <input v-model="filtros.busca" class="campo-input campo-input--busca" type="search"
                       placeholder="Buscar por CNPJ ou nome" autocomplete="off">
              </div>
              <select v-model="porPagina" class="campo-input campo-input--pp">
                <option :value="10">10 por página</option>
                <option :value="25">25 por página</option>
                <option :value="50">50 por página</option>
                <option :value="100">100 por página</option>
              </select>
            </div>
          </div>

          <div class="table-wrapper">
            <table v-if="registrosPagina.length > 0" class="nf-table">
              <thead>
              <tr>
                <th
                    v-for="coluna in colunas"
                    :key="coluna.campo"
                    :class="[coluna.classe, 'th-ordenavel', { 'th-ativo': ordenacao.campo === coluna.campo }]"
                    @click="ordenarPor(coluna.campo)"
                >
                  {{ coluna.label }}
                  <span class="th-seta" :class="setaClasse(coluna.campo)"/>
                </th>
                <th class="th-center">Prioridade</th>
                <th class="th-center"></th>
              </tr>
              </thead>
              <tbody>
              <tr
                  v-for="registro in registrosPagina"
                  :key="registro.chave"
                  :class="`row--${registro.prioridade}`"
              >
                <td class="td-mono">{{ registro.cnpjFormatado }}</td>
                <td>
                  <div class="cell-stack">
                    <span class="cell-primary" :class="{ 'cell-primary--ausente': !registro.cadastrada }">
                      {{ registro.nomeExibicao }}
                    </span>
                    <span v-if="!registro.cadastrada" class="tag-nao-cadastrada">Sem cadastro no sistema</span>
                  </div>
                </td>
                <td class="td-center">
                  <span class="pill-divergencias">{{ formatInteiro(registro.divergencias) }}</span>
                </td>
                <td class="td-right td-valor">{{ formatMoeda(registro.diferenca) }}</td>
                <td class="td-right td-potencial">{{ formatMoeda(registro.potencial) }}</td>
                <td class="td-center">
                  <span class="prioridade-badge" :class="`prioridade-badge--${registro.prioridade}`">
                    {{ prioridadeLabel(registro.prioridade) }}
                  </span>
                </td>
                <td class="td-action">
                  <button
                      v-if="registro.atividade"
                      type="button"
                      class="btn-selecionar"
                      @click="verDetalhe(registro)"
                  >
                    Visualizar
                  </button>
                  <span v-else class="btn-selecionar btn-selecionar--off" title="Empresa não cadastrada no sistema">
                    Indisponível
                  </span>
                </td>
              </tr>
              </tbody>
              <tfoot>
              <tr>
                <td colspan="3" class="tf-label">Total dos registros filtrados</td>
                <td class="td-center tf-value">{{ formatInteiro(totalDivergencias) }}</td>
                <td class="td-right tf-value">{{ formatMoeda(totalDiferenca) }}</td>
                <td class="td-right tf-value tf-value--destaque">{{ formatMoeda(totalPotencial) }}</td>
                <td colspan="2"/>
              </tr>
              </tfoot>
            </table>

            <!-- Estado vazio -->
            <div v-else class="empty-state">
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                <circle cx="18" cy="18" r="17" stroke="#e2e8f0" stroke-width="1.5"/>
                <path d="M12 18h12M18 12v12" stroke="#cbd5e1" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
              <p v-if="registros.length === 0">Nenhuma empresa com divergência no Simples Nacional</p>
              <p v-else>Nenhum registro encontrado para os filtros aplicados</p>
              <button v-if="registros.length > 0 && filtrosAtivos > 0" type="button" class="btn-limpar"
                      @click="limparFiltros">
                Limpar filtros
              </button>
            </div>
          </div>

          <div v-if="registrosFiltrados.length > 0" class="table-footer">
            <span class="table-footer__info">
              Exibindo {{ formatInteiro(primeiroDaPagina) }}–{{ formatInteiro(ultimoDaPagina) }}
              de {{ formatInteiro(registrosFiltrados.length) }}
            </span>
            <paginate
                v-if="totalPaginas > 1"
                v-model="pagina"
                :page-count="totalPaginas"
                :page-range="3"
                :margin-pages="2"
                :click-handler="proximaPagina"
                :prev-text="'Anterior'"
                :next-text="'Próxima'"
                :prev-link-class="'page-link'"
                :next-link-class="'page-link'"
                :page-link-class="'page-link'"
                :container-class="'pagination pagination-rounded mb-0'"
                :page-class="'page-item'">
            </paginate>
          </div>
        </div>
      </template>
    </div>
  </Layout>
</template>

<script>
import * as echarts from 'echarts';
import Paginate from 'vuejs-paginate';
import Layout from "@/router/layouts/main";
import appConfig from "@/dados/app.config.json";
import Aguarde from "@/components/widgets/aguarde.vue";
import {list_simples_nacional_potencial} from "@/api/admin/fiscalizacao/simples_nacional";

const FAIXAS_DIVERGENCIA = [
  {label: '1 a 5', min: 1, max: 5},
  {label: '6 a 10', min: 6, max: 10},
  {label: '11 a 20', min: 11, max: 20},
  {label: '21 a 50', min: 21, max: 50},
  {label: 'Mais de 50', min: 51, max: Infinity},
];

const TOP_LIMITE = 10;

const TOOLTIP_BASE = {
  backgroundColor: '#1e293b',
  borderColor: '#334155',
  borderWidth: 0.5,
  textStyle: {color: '#e2e8f0', fontSize: 12, fontFamily: 'DM Sans, sans-serif'},
};

export default {
  page: {
    title: "Fiscalização do Simples Nacional",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  components: {
    Aguarde,
    Layout,
    Paginate,
  },
  data() {
    return {
      title: "Fiscalização do Simples Nacional",
      items: [
        {
          text: 'Painel',
          href: '/painel',
        },
        {
          text: 'Fiscalização do Simples Nacional',
          active: true,
        },
      ],
      httpSend: false,
      carregando: true,
      erro: null,
      ultimaAtualizacao: null,
      empresas: [],
      filtros: {
        busca: "",
        cnpj: "",
        nome: "",
        atividade: "",
        minDivergencias: "",
        minDiferenca: "",
        minPotencial: "",
        cadastro: "todos",
      },
      colunas: [
        {campo: 'cnpj', label: 'CNPJ / CPF', classe: 'th-cnpj'},
        {campo: 'empresa', label: 'Empresa'},
        {campo: 'divergencias', label: 'Divergências', classe: 'th-center'},
        {campo: 'diferenca', label: 'Diferença', classe: 'th-right'},
        {campo: 'potencial', label: 'Potencial', classe: 'th-right'},
      ],
      ordenacao: {campo: 'potencial', direcao: 'desc'},
      pagina: 1,
      porPagina: 50,
      charts: null,
      resizeObserver: null,
      elementoObservado: null,
    };
  },
  computed: {
    // ─── Normalização dos dados da API ────────────────────
    registros() {
      return (this.empresas || []).map((item, indice) => {
        const cnpj = this.somenteDigitos(item.esr_cnpj);
        const nome = this.textoOuNulo(item.pes_nome);
        const atividade = this.textoOuNulo(item.eco_codigo);
        const nomeExibicao = nome || 'Empresa não cadastrada';
        return {
          chave: `${cnpj || 'sem-cnpj'}-${indice}`,
          cnpj,
          cnpjFormatado: this.formatCnpj(cnpj),
          nome,
          nomeExibicao,
          atividade,
          atividadeExibicao: atividade || 'Não cadastrada',
          cadastrada: !!nome && !!atividade,
          divergencias: this.paraNumero(item.count),
          diferenca: this.paraNumero(item.esr_diferenca),
          potencial: this.paraNumero(item.esr_potencial),
          buscavel: this.normalizarTexto(nomeExibicao),
        };
      });
    },
    // ─── Filtros ──────────────────────────────────────────
    registrosFiltrados() {
      const busca = this.normalizarTexto(this.filtros.busca);
      const buscaDigitos = this.somenteDigitos(this.filtros.busca);
      const cnpj = this.somenteDigitos(this.filtros.cnpj);
      const nome = this.normalizarTexto(this.filtros.nome);
      const atividade = this.normalizarTexto(this.filtros.atividade);
      const minDivergencias = this.paraNumeroOuNulo(this.filtros.minDivergencias);
      const minDiferenca = this.paraNumeroOuNulo(this.filtros.minDiferenca);
      const minPotencial = this.paraNumeroOuNulo(this.filtros.minPotencial);
      const cadastro = this.filtros.cadastro;

      return this.registros.filter(registro => {
        if (busca) {
          const casaNome = registro.buscavel.indexOf(busca) !== -1;
          const casaCnpj = buscaDigitos ? registro.cnpj.indexOf(buscaDigitos) !== -1 : false;
          if (!casaNome && !casaCnpj) return false;
        }
        if (cnpj && registro.cnpj.indexOf(cnpj) === -1) return false;
        if (nome && registro.buscavel.indexOf(nome) === -1) return false;
        if (atividade && this.normalizarTexto(registro.atividadeExibicao).indexOf(atividade) === -1) return false;
        if (minDivergencias !== null && registro.divergencias < minDivergencias) return false;
        if (minDiferenca !== null && registro.diferenca < minDiferenca) return false;
        if (minPotencial !== null && registro.potencial < minPotencial) return false;
        if (cadastro === 'cadastradas' && !registro.cadastrada) return false;
        if (cadastro === 'nao_cadastradas' && registro.cadastrada) return false;
        return true;
      });
    },
    filtrosAtivos() {
      let total = 0;
      if (this.filtros.busca) total++;
      if (this.filtros.cnpj) total++;
      if (this.filtros.nome) total++;
      if (this.filtros.atividade) total++;
      if (this.filtros.minDivergencias !== "") total++;
      if (this.filtros.minDiferenca !== "") total++;
      if (this.filtros.minPotencial !== "") total++;
      if (this.filtros.cadastro !== 'todos') total++;
      return total;
    },
    // ─── Prioridade (percentis do conjunto filtrado) ──────
    limitesPrioridade() {
      const valores = this.registrosFiltrados
          .map(registro => registro.potencial)
          .filter(valor => valor > 0)
          .sort((a, b) => a - b);
      if (!valores.length) return {alta: Infinity, media: Infinity};
      return {
        alta: this.percentil(valores, 0.8),
        media: this.percentil(valores, 0.5),
      };
    },
    // ─── Ordenação e paginação ────────────────────────────
    registrosOrdenados() {
      const {campo, direcao} = this.ordenacao;
      const fator = direcao === 'asc' ? 1 : -1;
      return this.registrosFiltrados.slice().sort((a, b) => {
        const va = this.valorOrdenacao(a, campo);
        const vb = this.valorOrdenacao(b, campo);
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * fator;
        return String(va).localeCompare(String(vb), 'pt-BR', {numeric: true, sensitivity: 'base'}) * fator;
      });
    },
    registrosPagina() {
      const inicio = (this.pagina - 1) * this.porPagina;
      return this.registrosOrdenados
          .slice(inicio, inicio + this.porPagina)
          .map(registro => ({
            ...registro,
            prioridade: this.classificarPrioridade(registro.potencial),
          }));
    },
    totalPaginas() {
      return Math.ceil(this.registrosFiltrados.length / this.porPagina) || 0;
    },
    primeiroDaPagina() {
      if (!this.registrosFiltrados.length) return 0;
      return (this.pagina - 1) * this.porPagina + 1;
    },
    ultimoDaPagina() {
      return Math.min(this.pagina * this.porPagina, this.registrosFiltrados.length);
    },
    // ─── Totalizadores ────────────────────────────────────
    totalEmpresas() {
      return this.registrosFiltrados.length;
    },
    totalDivergencias() {
      return this.registrosFiltrados.reduce((total, registro) => total + registro.divergencias, 0);
    },
    totalDiferenca() {
      return this.registrosFiltrados.reduce((total, registro) => total + registro.diferenca, 0);
    },
    totalPotencial() {
      return this.registrosFiltrados.reduce((total, registro) => total + registro.potencial, 0);
    },
    totalNaoCadastradas() {
      return this.registrosFiltrados.filter(registro => !registro.cadastrada).length;
    },
    percentualNaoCadastradas() {
      if (!this.totalEmpresas) return 0;
      return (this.totalNaoCadastradas / this.totalEmpresas) * 100;
    },
    percentualPotencialFormatado() {
      if (!this.totalDiferenca) return '—';
      return this.formatPercentual((this.totalPotencial / this.totalDiferenca) * 100) + ' da diferença';
    },
    mediaDivergencias() {
      if (!this.totalEmpresas) return 0;
      return this.totalDivergencias / this.totalEmpresas;
    },
    maiorPotencial() {
      return this.registrosFiltrados.reduce((maior, registro) => {
        if (!maior || registro.potencial > maior.potencial) return registro;
        return maior;
      }, null);
    },
    indicadores() {
      return [
        {
          key: 'empresas',
          label: 'Empresas com divergências',
          value: `${this.formatInteiro(this.totalEmpresas)} ${this.totalEmpresas === 1 ? 'empresa' : 'empresas'}`,
          sub: 'identificadas na apuração',
          icon: this.iconEmpresa(),
          color: 'blue',
        },
        {
          key: 'divergencias',
          label: 'Total de divergências',
          value: `${this.formatInteiro(this.totalDivergencias)} ${this.totalDivergencias === 1 ? 'divergência' : 'divergências'}`,
          sub: `média de ${this.formatDecimal(this.mediaDivergencias)} por empresa`,
          icon: this.iconAlerta(),
          color: 'amber',
        },
        {
          key: 'diferenca',
          label: 'Total das diferenças',
          value: this.formatMoeda(this.totalDiferenca),
          sub: 'somatório das diferenças apuradas',
          icon: this.iconChart(),
          color: 'teal',
        },
        {
          key: 'nao_cadastradas',
          label: 'Empresas não cadastradas',
          value: this.formatInteiro(this.totalNaoCadastradas),
          sub: `${this.formatPercentual(this.percentualNaoCadastradas)} do total filtrado`,
          icon: this.iconCadastro(),
          color: 'red',
          acao: this.totalNaoCadastradas > 0 ? this.filtrarNaoCadastradas : null,
        },
      ];
    },
    // ─── Dados dos gráficos ───────────────────────────────
    topPotencial() {
      return this.registrosFiltrados
          .filter(registro => registro.potencial > 0)
          .sort((a, b) => b.potencial - a.potencial)
          .slice(0, TOP_LIMITE);
    },
    topDiferenca() {
      return this.registrosFiltrados
          .filter(registro => registro.diferenca > 0)
          .sort((a, b) => b.diferenca - a.diferenca)
          .slice(0, TOP_LIMITE);
    },
    faixasDivergencia() {
      return FAIXAS_DIVERGENCIA.map(faixa => {
        const registros = this.registrosFiltrados.filter(
            registro => registro.divergencias >= faixa.min && registro.divergencias <= faixa.max
        );
        return {
          label: faixa.label,
          empresas: registros.length,
          divergencias: registros.reduce((total, registro) => total + registro.divergencias, 0),
          potencial: registros.reduce((total, registro) => total + registro.potencial, 0),
        };
      });
    },
    pontosRelacao() {
      return this.registrosFiltrados
          .filter(registro => registro.diferenca > 0 || registro.potencial > 0)
          .map(registro => [registro.diferenca, registro.potencial, registro.divergencias, registro]);
    },
  },
  watch: {
    filtros: {
      deep: true,
      handler() {
        this.pagina = 1;
      },
    },
    porPagina() {
      this.pagina = 1;
    },
    ordenacao: {
      deep: true,
      handler() {
        this.pagina = 1;
      },
    },
    totalPaginas(valor) {
      if (valor > 0 && this.pagina > valor) this.pagina = valor;
    },
    registrosFiltrados() {
      this.renderizarGraficos();
    },
  },
  created() {
    this.charts = {};
    this.pesquisar();
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    Object.keys(this.charts || {}).forEach(chave => {
      const grafico = this.charts[chave];
      if (grafico && !grafico.isDisposed()) grafico.dispose();
    });
    this.charts = {};
  },
  methods: {
    // ─── Obtenção dos dados ───────────────────────────────
    pesquisar() {
      this.httpSend = true;
      this.erro = null;
      list_simples_nacional_potencial().then(result => {
        if (result && result.status) {
          this.empresas = Array.isArray(result.dados) ? result.dados : [];
          this.ultimaAtualizacao = new Date().toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'});
        } else {
          this.empresas = [];
          this.erro = (result && result.mensagem) || 'A consulta não retornou dados válidos.';
        }
      }).catch(error => {
        console.error(error);
        this.empresas = [];
        this.erro = 'Falha de comunicação com o servidor. Verifique sua conexão e tente novamente.';
      }).finally(() => {
        this.httpSend = false;
        this.carregando = false;
        this.renderizarGraficos();
      });
    },
    // ─── Conversão e formatação ───────────────────────────
    paraNumero(valor) {
      if (valor === null || valor === undefined || valor === '') return 0;
      const numero = Number(valor);
      return isNaN(numero) ? 0 : numero;
    },
    paraNumeroOuNulo(valor) {
      if (valor === null || valor === undefined || valor === '') return null;
      const numero = Number(valor);
      return isNaN(numero) ? null : numero;
    },
    textoOuNulo(valor) {
      if (valor === null || valor === undefined) return null;
      const texto = String(valor).trim();
      return texto === '' ? null : texto;
    },
    somenteDigitos(valor) {
      if (valor === null || valor === undefined) return '';
      return String(valor).replace(/\D/g, '');
    },
    normalizarTexto(valor) {
      if (valor === null || valor === undefined) return '';
      return String(valor)
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .trim()
          .toUpperCase();
    },
    formatMoeda(valor) {
      return this.paraNumero(valor).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatInteiro(valor) {
      return this.paraNumero(valor).toLocaleString('pt-BR', {maximumFractionDigits: 0});
    },
    formatDecimal(valor) {
      return this.paraNumero(valor).toLocaleString('pt-BR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });
    },
    formatPercentual(valor) {
      return `${this.paraNumero(valor).toLocaleString('pt-BR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`;
    },
    formatCnpj(valor) {
      const digitos = this.somenteDigitos(valor);
      if (digitos.length === 11) {
        return digitos.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
      }
      if (digitos.length === 14) {
        return digitos.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
      }
      return digitos || '—';
    },
    formatEixoMoeda(valor) {
      const numero = this.paraNumero(valor);
      const absoluto = Math.abs(numero);
      if (absoluto >= 1000000) return `R$ ${(numero / 1000000).toFixed(1)}M`;
      if (absoluto >= 1000) return `R$ ${(numero / 1000).toFixed(0)}k`;
      return `R$ ${numero.toFixed(0)}`;
    },
    truncar(texto, limite) {
      const valor = String(texto || '');
      return valor.length > limite ? `${valor.slice(0, limite - 1)}…` : valor;
    },
    rotuloEmpresa(registro) {
      return `${this.truncar(registro.nomeExibicao, 24)}\n${registro.cnpjFormatado}`;
    },
    // ─── Prioridade ───────────────────────────────────────
    percentil(ordenados, fracao) {
      if (!ordenados.length) return 0;
      const posicao = (ordenados.length - 1) * fracao;
      const inferior = Math.floor(posicao);
      const superior = Math.ceil(posicao);
      if (inferior === superior) return ordenados[inferior];
      return ordenados[inferior] + (ordenados[superior] - ordenados[inferior]) * (posicao - inferior);
    },
    classificarPrioridade(potencial) {
      if (!(potencial > 0)) return 'baixa';
      const limites = this.limitesPrioridade;
      if (potencial >= limites.alta) return 'alta';
      if (potencial >= limites.media) return 'media';
      return 'baixa';
    },
    prioridadeLabel(prioridade) {
      if (prioridade === 'alta') return 'Alta';
      if (prioridade === 'media') return 'Média';
      return 'Baixa';
    },
    // ─── Ordenação ────────────────────────────────────────
    valorOrdenacao(registro, campo) {
      switch (campo) {
        case 'cnpj':
          return registro.cnpj;
        case 'empresa':
          return registro.nomeExibicao;
        case 'atividade':
          return registro.atividadeExibicao;
        case 'divergencias':
          return registro.divergencias;
        case 'diferenca':
          return registro.diferenca;
        default:
          return registro.potencial;
      }
    },
    ordenarPor(campo) {
      if (this.ordenacao.campo === campo) {
        this.ordenacao = {campo, direcao: this.ordenacao.direcao === 'asc' ? 'desc' : 'asc'};
        return;
      }
      const textuais = ['cnpj', 'empresa', 'atividade'];
      this.ordenacao = {campo, direcao: textuais.indexOf(campo) !== -1 ? 'asc' : 'desc'};
    },
    setaClasse(campo) {
      if (this.ordenacao.campo !== campo) return 'th-seta--neutra';
      return this.ordenacao.direcao === 'asc' ? 'th-seta--asc' : 'th-seta--desc';
    },
    // ─── Filtros ──────────────────────────────────────────
    limparFiltros() {
      this.filtros = {
        busca: "",
        cnpj: "",
        nome: "",
        atividade: "",
        minDivergencias: "",
        minDiferenca: "",
        minPotencial: "",
        cadastro: "todos",
      };
    },
    filtrarNaoCadastradas() {
      this.filtros.cadastro = this.filtros.cadastro === 'nao_cadastradas' ? 'todos' : 'nao_cadastradas';
    },
    // ─── Navegação ────────────────────────────────────────
    proximaPagina(numeroPagina) {
      this.$refs.tabela.scrollIntoView({behavior: 'smooth', block: 'start'})
      this.pagina = parseInt(numeroPagina, 10) || 1;
    },
    verDetalhe(registro) {
      if (!registro.atividade) return;
      this.$router.push({
        name: 'admin-fiscalizacao-simples-nacional-apuracao',
        params: {eco_cnpj: registro.cnpj, eco_codigo: registro.atividade},
      });
    },
    // ─── Gráficos ─────────────────────────────────────────
    renderizarGraficos() {
      if (this.carregando) return;
      this.$nextTick(() => {
        if (!this.registrosFiltrados.length) return;
        this.montarChart('chartPotencial', 'potencial', this.opcaoTopValores(this.topPotencial, 'potencial'));
        this.montarChart('chartDiferenca', 'diferenca', this.opcaoTopValores(this.topDiferenca, 'diferenca'));
        this.montarChart('chartFaixas', 'faixas', this.opcaoFaixas());
        this.montarChart('chartRelacao', 'relacao', this.opcaoRelacao());
        this.observarRedimensionamento();
      });
    },
    montarChart(nomeRef, chave, opcao) {
      const elemento = this.$refs[nomeRef];
      if (!elemento) return;
      let grafico = this.charts[chave];
      // O container é recriado pelo v-if quando a série fica sem dados:
      // nesse caso a instância antiga aponta para um elemento descartado.
      if (grafico && (grafico.isDisposed() || grafico.getDom() !== elemento)) {
        if (!grafico.isDisposed()) grafico.dispose();
        grafico = null;
      }
      if (!grafico) {
        // Sem largura disponível o gráfico é criado com dimensões provisórias;
        // o ResizeObserver o redesenha assim que houver espaço.
        const dimensoes = elemento.clientWidth
            ? undefined
            : {width: 600, height: elemento.clientHeight || 260};
        grafico = echarts.init(elemento, null, dimensoes);
      }
      this.charts[chave] = grafico;
      grafico.setOption(opcao, true);
      grafico.resize();
    },
    redimensionarGraficos() {
      Object.keys(this.charts || {}).forEach(chave => {
        const grafico = this.charts[chave];
        if (grafico && !grafico.isDisposed()) grafico.resize();
      });
    },
    observarRedimensionamento() {
      if (typeof ResizeObserver === 'undefined') return;
      const alvo = this.$refs.chartsGrid;
      if (!alvo || alvo === this.elementoObservado) return;
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      } else {
        this.resizeObserver = new ResizeObserver(() => this.redimensionarGraficos());
      }
      this.resizeObserver.observe(alvo);
      this.elementoObservado = alvo;
    },
    opcaoTopValores(registros, campo) {
      const ehPotencial = campo === 'potencial';
      const cores = ehPotencial
          ? [{offset: 0, color: '#4f6ef7'}, {offset: 1, color: '#818cf8'}]
          : [{offset: 0, color: '#f59e0b'}, {offset: 1, color: '#fbbf24'}];
      return {
        animationDuration: 700,
        animationEasing: 'cubicOut',
        grid: {top: 8, right: 96, bottom: 8, left: 8, containLabel: true},
        tooltip: {
          ...TOOLTIP_BASE,
          trigger: 'item',
          formatter: params => this.tooltipEmpresa(registros[params.dataIndex]),
        },
        xAxis: {
          type: 'value',
          axisLine: {show: false},
          axisTick: {show: false},
          splitLine: {lineStyle: {color: '#f1f5f9', type: 'dashed'}},
          axisLabel: {
            color: '#94a3b8',
            fontSize: 11,
            fontFamily: 'DM Sans, sans-serif',
            formatter: valor => this.formatEixoMoeda(valor),
          },
        },
        yAxis: {
          type: 'category',
          inverse: true,
          data: registros.map(registro => this.rotuloEmpresa(registro)),
          axisTick: {show: false},
          axisLine: {lineStyle: {color: '#e2e8f0'}},
          axisLabel: {
            lineHeight: 14,
            formatter: valor => {
              const partes = String(valor).split('\n');
              return `{nome|${partes[0]}}\n{cnpj|${partes[1] || ''}}`;
            },
            rich: {
              nome: {color: '#334155', fontSize: 11, fontFamily: 'DM Sans, sans-serif', align: 'right'},
              cnpj: {color: '#94a3b8', fontSize: 10, fontFamily: 'DM Mono, monospace', align: 'right'},
            },
          },
        },
        series: [
          {
            type: 'bar',
            data: registros.map(registro => (ehPotencial ? registro.potencial : registro.diferenca)),
            barMaxWidth: 16,
            itemStyle: {
              borderRadius: [0, 4, 4, 0],
              color: {type: 'linear', x: 0, y: 0, x2: 1, y2: 0, colorStops: cores},
            },
            label: {
              show: true,
              position: 'right',
              color: '#334155',
              fontSize: 11,
              fontFamily: 'DM Sans, sans-serif',
              formatter: params => this.formatEixoMoeda(params.value),
            },
          },
        ],
      };
    },
    opcaoFaixas() {
      const faixas = this.faixasDivergencia;
      return {
        animationDuration: 700,
        animationEasing: 'cubicOut',
        grid: {top: 24, right: 16, bottom: 24, left: 8, containLabel: true},
        tooltip: {
          ...TOOLTIP_BASE,
          trigger: 'axis',
          axisPointer: {type: 'shadow'},
          formatter: params => {
            const faixa = faixas[params[0].dataIndex];
            const percentual = this.totalEmpresas
                ? this.formatPercentual((faixa.empresas / this.totalEmpresas) * 100)
                : '0,0%';
            return `
              <div style="font-size:11px;color:#94a3b8;margin-bottom:8px;font-weight:500">${faixa.label} divergências</div>
              ${this.linhaTooltip('Empresas', `${this.formatInteiro(faixa.empresas)} (${percentual})`)}
              ${this.linhaTooltip('Divergências', this.formatInteiro(faixa.divergencias))}
              ${this.linhaTooltip('Potencial', this.formatMoeda(faixa.potencial))}
            `;
          },
        },
        xAxis: {
          type: 'category',
          data: faixas.map(faixa => faixa.label),
          axisTick: {show: false},
          axisLine: {lineStyle: {color: '#e2e8f0'}},
          axisLabel: {color: '#94a3b8', fontSize: 11, fontFamily: 'DM Sans, sans-serif'},
        },
        yAxis: {
          type: 'value',
          name: 'Empresas',
          nameTextStyle: {color: '#94a3b8', fontSize: 10},
          splitLine: {lineStyle: {color: '#f1f5f9', type: 'dashed'}},
          axisLabel: {
            color: '#94a3b8',
            fontSize: 11,
            fontFamily: 'DM Sans, sans-serif',
            formatter: valor => this.formatInteiro(valor),
          },
        },
        series: [
          {
            type: 'bar',
            data: faixas.map(faixa => faixa.empresas),
            barMaxWidth: 46,
            itemStyle: {
              borderRadius: [4, 4, 0, 0],
              color: {
                type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [{offset: 0, color: '#60a5fa'}, {offset: 1, color: '#bfdbfe'}],
              },
            },
            label: {
              show: true,
              position: 'top',
              color: '#64748b',
              fontSize: 10,
              fontFamily: 'DM Sans, sans-serif',
              formatter: params => this.formatInteiro(params.value),
            },
          },
        ],
      };
    },
    opcaoRelacao() {
      const pontos = this.pontosRelacao;
      return {
        animationDuration: 700,
        grid: {top: 24, right: 24, bottom: 32, left: 8, containLabel: true},
        tooltip: {
          ...TOOLTIP_BASE,
          trigger: 'item',
          formatter: params => this.tooltipEmpresa(params.data[3]),
        },
        xAxis: {
          type: 'value',
          name: 'Diferença',
          nameLocation: 'middle',
          nameGap: 28,
          nameTextStyle: {color: '#94a3b8', fontSize: 10},
          splitLine: {lineStyle: {color: '#f1f5f9', type: 'dashed'}},
          axisLine: {show: false},
          axisTick: {show: false},
          axisLabel: {
            color: '#94a3b8',
            fontSize: 11,
            fontFamily: 'DM Sans, sans-serif',
            formatter: valor => this.formatEixoMoeda(valor),
          },
        },
        yAxis: {
          type: 'value',
          name: 'Potencial',
          nameTextStyle: {color: '#94a3b8', fontSize: 10},
          splitLine: {lineStyle: {color: '#f1f5f9', type: 'dashed'}},
          axisLabel: {
            color: '#94a3b8',
            fontSize: 11,
            fontFamily: 'DM Sans, sans-serif',
            formatter: valor => this.formatEixoMoeda(valor),
          },
        },
        series: [
          {
            type: 'scatter',
            data: pontos,
            symbolSize: dados => Math.min(20, 6 + Math.sqrt(this.paraNumero(dados[2])) * 1.6),
            itemStyle: {
              color: 'rgba(79,110,247,0.55)',
              borderColor: '#4f6ef7',
              borderWidth: 0.5,
            },
            emphasis: {itemStyle: {color: '#4f6ef7'}},
          },
        ],
      };
    },
    tooltipEmpresa(registro) {
      if (!registro) return '';
      return `
        <div style="font-size:12px;color:#e2e8f0;margin-bottom:2px;font-weight:600;max-width:260px">
          ${this.escaparHtml(this.truncar(registro.nomeExibicao, 40))}
        </div>
        <div style="font-size:11px;color:#94a3b8;margin-bottom:8px">
          CNPJ ${registro.cnpjFormatado} · Código ${this.escaparHtml(registro.atividadeExibicao)}
        </div>
        ${this.linhaTooltip('Divergências', this.formatInteiro(registro.divergencias))}
        ${this.linhaTooltip('Diferença', this.formatMoeda(registro.diferenca))}
        ${this.linhaTooltip('Potencial', this.formatMoeda(registro.potencial))}
      `;
    },
    linhaTooltip(rotulo, valor) {
      return `
        <div style="display:flex;align-items:center;gap:14px;margin-bottom:3px">
          <span style="color:#94a3b8;font-size:11px">${rotulo}</span>
          <span style="margin-left:auto;font-weight:600">${valor}</span>
        </div>
      `;
    },
    escaparHtml(texto) {
      return String(texto || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
    },
    // ─── Ícones ───────────────────────────────────────────
    iconMoney() {
      return `<svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="2" y="5" width="14" height="9" rx="2" stroke="currentColor" stroke-width="1.4"/>
        <circle cx="9" cy="9.5" r="2" stroke="currentColor" stroke-width="1.4"/>
        <path d="M5 5V4M13 5V4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
      </svg>`;
    },
    iconEmpresa() {
      return `<svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="2.5" y="3" width="13" height="12" rx="1.6" stroke="currentColor" stroke-width="1.4"/>
        <path d="M6 6.5h2M6 9h2M10 6.5h2M10 9h2M7.5 15v-2.5h3V15" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
      </svg>`;
    },
    iconAlerta() {
      return `<svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M9 2.6l6.4 11.2A1 1 0 0114.5 15.3h-11a1 1 0 01-.87-1.5L9 2.6z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>
        <path d="M9 7v3.2M9 12.6h.01" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
      </svg>`;
    },
    iconChart() {
      return `<svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M3 13l4-4 3 3 5-6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    },
    iconCadastro() {
      return `<svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="3" y="2.5" width="12" height="13" rx="1.6" stroke="currentColor" stroke-width="1.4"/>
        <path d="M6 6.5h6M6 9h4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
        <path d="M6.8 12.2l3.4 0" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
      </svg>`;
    },
  },
};
</script>

<style scoped>
/* ─── Base ───────────────────────────────────────────────── */
.fisc-sn {
  width: 100%;
  color: #0f172a;
}

/* ─── Cabeçalho ──────────────────────────────────────────── */
.page-intro {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.25rem;
}

.page-intro__title {
  margin: 0 0 2px;
  font-size: 19px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #0f172a;
}

.page-intro__sub {
  margin: 0;
  font-size: 13px;
  color: #94a3b8;
}

.page-intro__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.badge-live {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 999px;
  padding: 5px 12px;
}

.live-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
}

.btn-atualizar {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  color: #334155;
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  padding: 7px 14px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.btn-atualizar:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: #94a3b8;
}

.btn-atualizar:disabled {
  opacity: 0.6;
  cursor: default;
}

/* ─── Erro ───────────────────────────────────────────────── */
.alert-erro {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fff8f8;
  border: 1px solid #fee2e2;
  border-left: 4px solid #ef4444;
  border-radius: 10px;
  padding: 14px 1.25rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}

.alert-erro__icon {
  color: #dc2626;
  display: flex;
  flex-shrink: 0;
}

.alert-erro__body {
  flex: 1;
  min-width: 200px;
}

.alert-erro__title {
  margin: 0 0 2px;
  font-size: 13px;
  font-weight: 600;
  color: #991b1b;
}

.alert-erro__desc {
  margin: 0;
  font-size: 12px;
  color: #b91c1c;
}

.alert-erro__btn {
  background: #dc2626;
  border: 1px solid #dc2626;
  border-radius: 7px;
  color: #fff;
  padding: 7px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.alert-erro__btn:disabled {
  opacity: 0.6;
  cursor: default;
}

/* ─── Skeleton ───────────────────────────────────────────── */
.sk-metrics,
.sk-charts {
  display: grid;
  gap: 14px;
  margin-bottom: 1.25rem;
}

.sk-metrics {
  grid-template-columns: repeat(4, 1fr);
}

.sk-charts {
  grid-template-columns: 1fr 1fr;
}

.sk-metric,
.sk-chart,
.sk-row {
  background: linear-gradient(90deg, #eef2f6 25%, #f8fafc 50%, #eef2f6 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  border-radius: 12px;
}

.sk-metric {
  height: 132px;
}

.sk-metric--wide {
  grid-column: span 2;
}

.sk-chart {
  height: 220px;
}

.sk-table {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sk-row {
  height: 36px;
  border-radius: 8px;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ─── Indicadores ────────────────────────────────────────── */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 1.25rem;
}

.metric-card {
  background: #fff;
  border: 0.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  transition: box-shadow 0.15s;
}

.metric-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.metric-card--clicavel {
  cursor: pointer;
}

.metric-icon {
  width: 36px;
  height: 36px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.metric-card--blue .metric-icon {
  background: #eff6ff;
  color: #3b82f6;
}

.metric-card--amber .metric-icon {
  background: #fffbeb;
  color: #f59e0b;
}

.metric-card--teal .metric-icon {
  background: #f0fdfa;
  color: #14b8a6;
}

.metric-card--red .metric-icon {
  background: #fef2f2;
  color: #ef4444;
}

.metric-label {
  margin: 0 0 2px;
  font-size: 12px;
  color: #94a3b8;
}

.metric-value {
  margin: 0 0 2px;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.03em;
  color: #0f172a;
}

.metric-sub {
  margin: 0;
  font-size: 11px;
  color: #a4a9af;
}

/* Indicador principal */
.metric-card--destaque {
  grid-column: span 2;
  background: linear-gradient(135deg, #1e293b 0%, #312e81 100%);
  border: none;
  color: #e2e8f0;
  justify-content: space-between;
}

.metric-card--destaque:hover {
  box-shadow: 0 8px 24px rgba(49, 46, 129, 0.22);
}

.metric-destaque__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.metric-icon--destaque {
  background: rgba(255, 255, 255, 0.12);
  color: #c7d2fe;
}

.metric-destaque__tag {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #c7d2fe;
  background: rgba(199, 210, 254, 0.14);
  border-radius: 999px;
  padding: 3px 10px;
}

.metric-destaque__label {
  margin: 0 0 2px;
  font-size: 12px;
  color: #a5b4fc;
}

.metric-destaque__value {
  margin: 0 0 4px;
  font-size: 34px;
  font-weight: 700;
  letter-spacing: -0.035em;
  color: #fff;
  line-height: 1.1;
}

.metric-destaque__desc {
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
}

.metric-destaque__footer {
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 10px;
  min-width: 0;
}

.metric-destaque__footer-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #818cf8;
}

.metric-destaque__footer-cnpj {
  font-family: 'DM Mono', monospace;
  font-size: 11px;
  color: #a5b4fc;
}

.metric-destaque__footer-value {
  font-size: 12px;
  font-weight: 500;
  color: #e2e8f0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ─── Gráficos ───────────────────────────────────────────── */
.charts-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 1.25rem;
  min-width: 0;
}

.chart-card {
  background: #fff;
  border: 0.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  min-width: 0;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 1rem;
}

.chart-title {
  margin: 0 0 2px;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}

.chart-sub {
  margin: 0;
  font-size: 12px;
  color: #a4a9af;
}

.chart-total {
  text-align: right;
  white-space: nowrap;
}

.chart-total-label {
  margin: 0 0 2px;
  font-size: 11px;
  color: #a4a9af;
}

.chart-total-value {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #4f6ef7;
  letter-spacing: -0.02em;
}

.chart-container {
  height: 260px;
  width: 100%;
}

.chart-vazio {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 160px;
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
}

.chart-container--tall {
  height: 380px;
}

/* ─── Filtros ────────────────────────────────────────────── */
.filtros-card {
  background: #fff;
  border: 0.5px solid #e2e8f0;
  border-radius: 12px;
  margin-bottom: 1.25rem;
  overflow: hidden;
}

.filtros-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 1rem 1.5rem;
  border-bottom: 0.5px solid #f1f5f9;
  flex-wrap: wrap;
}

.filtros-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px 14px;
  padding: 1.15rem 1.5rem;
}

.campo {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}

.campo label {
  margin: 0;
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.campo-input {
  width: 100%;
  border: 0.5px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  color: #334155;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.campo-input::placeholder {
  color: #cbd5e1;
}

.campo-input:focus {
  outline: none;
  border-color: #4f6ef7;
  box-shadow: 0 0 0 3px rgba(79, 110, 247, 0.12);
}

.btn-limpar {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  color: #64748b;
  border: 0.5px solid #e2e8f0;
  border-radius: 8px;
  padding: 7px 16px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.btn-limpar:hover {
  background: #f8fafc;
  color: #334155;
}

/* ─── Tabela ─────────────────────────────────────────────── */
.table-card {
  background: #fff;
  border: 0.5px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 0.5px solid #f1f5f9;
  flex-wrap: wrap;
  gap: 10px;
}

.header-left-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.header-right-group {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.badge-count {
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 999px;
  padding: 3px 10px;
}

.badge-count--ativo {
  color: #4f6ef7;
  background: #eef2ff;
}

.busca-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.busca-icon {
  position: absolute;
  left: 11px;
  color: #cbd5e1;
  pointer-events: none;
}

.campo-input--busca {
  width: 250px;
  padding-left: 32px;
  font-size: 12px;
}

.campo-input--pp {
  width: auto;
  font-size: 12px;
  padding: 8px 10px;
  color: #64748b;
}

.table-wrapper {
  overflow-x: auto;
}

.nf-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.nf-table thead tr {
  background: #f8fafc;
}

.nf-table th {
  padding: 9px 1rem;
  text-align: left;
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
}

.th-ordenavel {
  cursor: pointer;
  user-select: none;
  transition: color 0.15s;
}

.th-ordenavel:hover {
  color: #475569;
}

.th-ativo {
  color: #4f6ef7;
}

.th-seta {
  display: inline-block;
  width: 0;
  height: 0;
  margin-left: 5px;
  vertical-align: middle;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
}

.th-seta--neutra {
  border-top: 4px solid #e2e8f0;
}

.th-seta--asc {
  border-bottom: 4px solid #4f6ef7;
}

.th-seta--desc {
  border-top: 4px solid #4f6ef7;
}

.th-right,
.td-right {
  text-align: right;
}

.th-center,
.td-center {
  text-align: center;
}

.th-cnpj {
  width: 140px;
}

.nf-table td {
  padding: 9px 1rem;
  border-top: 0.5px solid #f1f5f9;
  color: #334155;
  vertical-align: middle;
}

.nf-table tbody tr {
  transition: background 0.12s;
}

.nf-table tbody tr:hover td {
  background: #f8fafc;
}

.row--alta td:first-child {
  box-shadow: inset 3px 0 0 #ef4444;
}

.row--media td:first-child {
  box-shadow: inset 3px 0 0 #f59e0b;
}

.cell-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.35;
}

.cell-primary {
  font-weight: 500;
  color: #0f172a;
}

.cell-primary--ausente {
  color: #94a3b8;
  font-style: italic;
}

.tag-nao-cadastrada {
  font-size: 10px;
  font-weight: 500;
  color: #b45309;
  background: #fffbeb;
  border-radius: 4px;
  padding: 1px 6px;
  align-self: flex-start;
}

.td-mono {
  font-family: 'DM Mono', monospace;
  font-size: 12px;
  color: #475569;
  white-space: nowrap;
}

.td-muted {
  color: #94a3b8;
}

.td-valor {
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
}

.td-potencial {
  font-weight: 700;
  font-size: 13px;
  color: #4338ca;
  white-space: nowrap;
}

.pill-divergencias {
  display: inline-block;
  min-width: 30px;
  padding: 2px 9px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #334155;
  font-size: 11px;
  font-weight: 600;
}

.prioridade-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  white-space: nowrap;
}

.prioridade-badge--alta {
  background: #fef2f2;
  color: #dc2626;
}

.prioridade-badge--media {
  background: #fffbeb;
  color: #d97706;
}

.prioridade-badge--baixa {
  background: #f1f5f9;
  color: #94a3b8;
}

.td-action {
  text-align: center;
  white-space: nowrap;
}

.btn-selecionar {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #fff;
  color: #4f6ef7;
  border: 0.5px solid #c7d2fe;
  border-radius: 7px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, transform 0.1s;
  white-space: nowrap;
}

.btn-selecionar:hover {
  background: #4f6ef7;
  color: #fff;
}

.btn-selecionar:active {
  transform: scale(0.97);
}

.btn-selecionar--off {
  color: #cbd5e1;
  border-color: #e2e8f0;
  cursor: not-allowed;
}

.btn-selecionar--off:hover {
  background: #fff;
  color: #cbd5e1;
}

.nf-table tfoot td {
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  padding: 10px 1rem;
}

.tf-label {
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.tf-value {
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
}

.tf-value--destaque {
  color: #4338ca;
  font-size: 13px;
}

.table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 0.9rem 1.5rem;
  border-top: 0.5px solid #f1f5f9;
}

.table-footer__info {
  font-size: 12px;
  color: #94a3b8;
}

/* ─── Estado vazio ───────────────────────────────────────── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 3rem;
  color: #94a3b8;
  font-size: 13px;
  text-align: center;
}

.empty-state p {
  margin: 0;
}

/* ─── Responsividade ─────────────────────────────────────── */
@media (max-width: 1200px) {
  .metrics-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .filtros-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 992px) {
  .charts-grid {
    grid-template-columns: 1fr;
  }

  .sk-charts {
    grid-template-columns: 1fr;
  }

  .sk-metrics {
    grid-template-columns: repeat(2, 1fr);
  }

  .filtros-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .campo-input--busca {
    width: 200px;
  }
}

@media (max-width: 640px) {
  .metrics-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .metric-card--destaque {
    grid-column: span 1;
  }

  .sk-metrics {
    grid-template-columns: 1fr;
  }

  .sk-metric--wide {
    grid-column: span 1;
  }

  .filtros-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .header-right-group {
    width: 100%;
  }

  .campo-input--busca {
    width: 100%;
  }

  .metric-destaque__value {
    font-size: 28px;
  }
}
</style>
